import { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAnalytics } from '@/lib/analytics'
import { getUtmParams } from '@/lib/utils'
import { claimAnonymousBrand, getStoredClaimSessionToken } from '@/lib/claimBrand'
import { autoDetectUserTimezone, shouldAutoDetectTimezone } from '@/lib/timezone-auto-detect'

export default function AuthCallback() {
    const navigate = useNavigate()
    const location = useLocation()
    const [isLoading, setIsLoading] = useState(true)
    const { trackEvent, events } = useAnalytics()

    console.log('AuthCallback component running')

    // Function to save UTM parameters to user profile
    const saveUtmParams = async (userId: string) => {
        const utmParams = getUtmParams()
        
        if (Object.keys(utmParams).length > 0) {
            try {
                // Get all UTM parameters including optional ones
                const updateData: Record<string, string> = {};
                
                // Handle standard UTM parameters
                if (utmParams.utm_source) updateData.utm_source = utmParams.utm_source;
                if (utmParams.utm_medium) updateData.utm_medium = utmParams.utm_medium;
                if (utmParams.utm_campaign) updateData.utm_campaign = utmParams.utm_campaign;
                if (utmParams.utm_term) updateData.utm_term = utmParams.utm_term;
                if (utmParams.utm_content) updateData.utm_content = utmParams.utm_content;
                
                // Get existing profile to check if we need to merge metadata
                const { data: existingProfile } = await supabase
                    .schema('marketing_and_customer_success')
                    .from('profiles')
                    .select('metadata')
                    .eq('id', userId)
                    .single()
                
                // Store all UTM parameters in metadata as well for future reference
                updateData.metadata = JSON.stringify({
                    ...((existingProfile?.metadata as any) || {}),
                    utm_params: utmParams,
                    utm_capture_date: new Date().toISOString()
                });
                
                // Update user profile with UTM parameters
                const { error } = await supabase
                    .schema('marketing_and_customer_success')
                    .from('profiles')
                    .update(updateData)
                    .eq('id', userId)
                
                if (error) {
                    console.error('Error saving UTM parameters:', error)
                } else {
                    console.log('Successfully saved UTM parameters for user', userId, updateData);
                }
            } catch (error) {
                console.error('Exception saving UTM parameters:', error)
            }
        }
    }

    useEffect(() => {
        const checkAuthStatus = async () => {
            console.log('[AuthCallback] Starting auth status check...')
            
            // Debug: Check current URL and parameters
            const currentUrl = window.location.href
            const urlParams = new URLSearchParams(window.location.search)
            const hashParams = new URLSearchParams(window.location.hash.replace('#', ''))
            console.log('[AuthCallback] Current URL:', currentUrl)
            console.log('[AuthCallback] URL params:', Object.fromEntries(urlParams.entries()))
            console.log('[AuthCallback] Hash params:', Object.fromEntries(hashParams.entries()))
            
            // Debug: Check for error parameters from Supabase
            const error = urlParams.get('error')
            const errorDescription = urlParams.get('error_description')
            if (error) {
                console.error('[AuthCallback] Auth error from Supabase:', error, errorDescription)
            }
            
            // Get session
            const { data: { session }, error: sessionError } = await supabase.auth.getSession()
            console.log('[AuthCallback] Session check result:', { 
                hasSession: !!session, 
                sessionError,
                userId: session?.user?.id,
                email: session?.user?.email 
            })
            
            if (session) {
                // Save UTM parameters to user profile
                await saveUtmParams(session.user.id)
                
                // Auto-detect timezone for first-time users
                try {
                    const needsTimezone = await shouldAutoDetectTimezone(session.user)
                    if (needsTimezone) {
                        console.log('[AuthCallback] Auto-detecting timezone for new user')
                        const timezoneResult = await autoDetectUserTimezone(session.user, {
                            updateProfile: true,
                            silentMode: false
                        })
                        
                        if (timezoneResult.detected && timezoneResult.timezone) {
                            console.log('[AuthCallback] Successfully auto-detected timezone:', timezoneResult.timezone)
                            // Track timezone auto-detection
                            trackEvent(events.FEATURE_USED, {
                                feature: 'timezone_auto_detection',
                                user_id: session.user.id,
                                detected_timezone: timezoneResult.timezone,
                                was_successful: true
                            })
                        } else if (timezoneResult.error) {
                            console.log('[AuthCallback] Timezone auto-detection failed:', timezoneResult.error)
                            trackEvent(events.ERROR_OCCURRED, {
                                error_type: 'timezone_auto_detection_failed',
                                user_id: session.user.id,
                                error_message: timezoneResult.error
                            })
                        }
                    }
                } catch (error) {
                    console.error('[AuthCallback] Error during timezone auto-detection:', error)
                    trackEvent(events.ERROR_OCCURRED, {
                        error_type: 'timezone_auto_detection_error',
                        user_id: session.user.id,
                        error_message: error instanceof Error ? error.message : 'Unknown error'
                    })
                }
                
                // Track successful authentication
                trackEvent(events.USER_LOGGED_IN, {
                    user_id: session.user.id,
                    email_domain: session.user.email?.split('@')[1] || 'unknown',
                    auth_provider: session.user.app_metadata.provider || 'unknown',
                    is_new_user: session.user.created_at === session.user.updated_at,
                    ...getUtmParams() // Add UTM parameters to tracking event
                })

                // Check if this is a brand claim flow BEFORE doing standard navigation
                const urlParams = new URLSearchParams(window.location.search)
                const isClaim = urlParams.get('claim') === 'true'
                const sessionToken = urlParams.get('sessionToken') || getStoredClaimSessionToken()
                
                if (isClaim && sessionToken) {
                    console.log('[AuthCallback] Brand claim flow detected in session check with existing session')
                    // Handle brand claiming directly here since user is already authenticated
                    try {
                        console.log('[AuthCallback] Processing brand claim for session token:', sessionToken)
                        const claimResult = await claimAnonymousBrand(sessionToken)
                        console.log('[AuthCallback] Brand claimed successfully! Result:', claimResult)
                        
                        // Track successful brand claim
                        trackEvent(events.BRAND_CREATED, {
                            user_id: session.user.id,
                            brand_id: claimResult.brand_id,
                            source: 'anonymous_claim',
                            ...getUtmParams()
                        })

                        // Redirect to onboarding with the claimed brand
                        const redirectUrl = `/onboarding?brandId=${claimResult.brand_id}`
                        console.log('[AuthCallback] 🎯 Redirecting to onboarding with claimed brand:', redirectUrl)
                        navigate(redirectUrl, { replace: true })
                        return
                    } catch (error) {
                        console.error('[AuthCallback] Failed to claim brand:', error)
                        // Continue with normal flow on failure
                    }
                }

                // Standard auth flow - no invitation token handling here
                // Invitation tokens are handled via direct redirects now

                // Standard flow for non-invitation users
                // Check onboarding status
                const { data: profile } = await supabase
                    .schema('marketing_and_customer_success')
                    .from('profiles')
                    .select('onboarding_completed')
                    .eq('id', session.user.id)
                    .single()

                if (!profile?.onboarding_completed) {
                    // Track onboarding start
                    trackEvent(events.USER_ONBOARDING_STARTED, {
                        user_id: session.user.id,
                        is_new_user: session.user.created_at === session.user.updated_at,
                        ...getUtmParams() // Add UTM parameters to tracking event
                    })
                    navigate('/onboarding')
                } else {
                    // Track returning user
                    trackEvent(events.USER_LOGGED_IN, {
                        user_id: session.user.id,
                        is_returning: true,
                        ...getUtmParams() // Add UTM parameters to tracking event
                    })
                    navigate('/dashboard')
                }
            } else {
                // No session found - let's check if we're in the middle of OAuth callback
                console.log('[AuthCallback] No session found in checkAuthStatus')
                
                // Check if there's a code or error in the URL (OAuth callback)
                const code = urlParams.get('code')
                const authError = urlParams.get('error')
                
                if (code) {
                    console.log('[AuthCallback] OAuth code found, waiting for auth state change...')
                    // Don't set loading to false yet, wait for auth state change
                    
                    // Try to exchange the code for a session
                    setTimeout(async () => {
                        console.log('[AuthCallback] Retrying session check after delay...')
                        const { data: { session: retrySession }, error: retryError } = await supabase.auth.getSession()
                        console.log('[AuthCallback] Retry session result:', { 
                            hasSession: !!retrySession, 
                            retryError,
                            userId: retrySession?.user?.id 
                        })
                        
                        if (!retrySession) {
                            console.log('[AuthCallback] Still no session after retry, auth likely failed')
                            setIsLoading(false)
                        }
                    }, 2000) // Wait 2 seconds for auth to complete
                } else if (authError) {
                    console.log('[AuthCallback] OAuth error:', authError, urlParams.get('error_description'))
                    // Track authentication failure
                    trackEvent(events.ERROR_OCCURRED, {
                        error_type: 'auth_failed',
                        reason: authError,
                        error_description: urlParams.get('error_description'),
                        ...getUtmParams()
                    })
                    setIsLoading(false)
                } else {
                    console.log('[AuthCallback] No session, no code, no error - auth failed')
                    // Track authentication failure
                    trackEvent(events.ERROR_OCCURRED, {
                        error_type: 'auth_failed',
                        reason: 'no_session',
                        ...getUtmParams()
                    })
                    setIsLoading(false)
                }
            }
        }

        const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
            console.log('[AuthCallback] Auth state change:', { 
                event, 
                hasSession: !!session,
                userId: session?.user?.id,
                email: session?.user?.email
            })
            
            if (event === 'SIGNED_IN' && session) {
                // Check for brand claiming context
                const urlParams = new URLSearchParams(window.location.search)
                const isClaim = urlParams.get('claim') === 'true'
                const sessionToken = urlParams.get('sessionToken') || getStoredClaimSessionToken()

                // Save UTM parameters to user profile
                await saveUtmParams(session.user.id)
                
                // Auto-detect timezone for first-time users
                try {
                    const needsTimezone = await shouldAutoDetectTimezone(session.user)
                    if (needsTimezone) {
                        console.log('[AuthCallback] Auto-detecting timezone for new user (auth listener)')
                        const timezoneResult = await autoDetectUserTimezone(session.user, {
                            updateProfile: true,
                            silentMode: false
                        })
                        
                        if (timezoneResult.detected && timezoneResult.timezone) {
                            console.log('[AuthCallback] Successfully auto-detected timezone (auth listener):', timezoneResult.timezone)
                            // Track timezone auto-detection
                            trackEvent(events.FEATURE_USED, {
                                feature: 'timezone_auto_detection',
                                user_id: session.user.id,
                                detected_timezone: timezoneResult.timezone,
                                was_successful: true,
                                context: 'auth_listener'
                            })
                        }
                    }
                } catch (error) {
                    console.error('[AuthCallback] Error during timezone auto-detection (auth listener):', error)
                }
                
                // Track sign-in event
                trackEvent(events.USER_LOGGED_IN, {
                    event_type: event,
                    user_id: session.user.id,
                    auth_provider: session.user.app_metadata.provider || 'unknown',
                    is_brand_claim: isClaim,
                    ...getUtmParams() // Add UTM parameters to tracking event
                })
                
                // Handle brand claiming flow
                if (isClaim && sessionToken) {
                    try {
                        console.log('[AuthCallback] Processing brand claim for session token in auth listener:', sessionToken)
                        const claimResult = await claimAnonymousBrand(sessionToken)
                        console.log('[AuthCallback] Brand claimed successfully in auth listener! Full result:', claimResult)
                        console.log('[AuthCallback] Claimed brand ID in auth listener:', claimResult.brand_id)
                        
                        // Track successful brand claim
                        trackEvent(events.BRAND_CREATED, {
                            user_id: session.user.id,
                            brand_id: claimResult.brand_id,
                            source: 'anonymous_claim',
                            ...getUtmParams()
                        })

                        // Redirect to onboarding with the claimed brand
                        const redirectUrl = `/onboarding?brandId=${claimResult.brand_id}`
                        console.log('[AuthCallback] 🎯 Redirecting to onboarding with claimed brand (auth listener):', redirectUrl)
                        setIsLoading(false) // Make sure to set loading to false before navigating
                        navigate(redirectUrl, { replace: true })
                        return
                    } catch (error) {
                        console.error('[AuthCallback] Failed to claim brand in auth listener:', error)
                        console.error('[AuthCallback] Error details in auth listener:', {
                            message: error instanceof Error ? error.message : String(error),
                            sessionToken,
                            isClaim
                        })
                        // Track failed brand claim
                        trackEvent(events.ERROR_OCCURRED, {
                            error_type: 'brand_claim_failed',
                            user_id: session.user.id,
                            error_message: error instanceof Error ? error.message : String(error),
                            ...getUtmParams()
                        })
                        // Continue with normal flow on failure
                    }
                }
                
                // Standard auth flow - no invitation token handling here
                // Invitation tokens are handled via direct redirects now
                
                // Check onboarding status
                const { data: profile } = await supabase
                    .schema('marketing_and_customer_success')
                    .from('profiles')
                    .select('onboarding_completed')
                    .eq('id', session.user.id)
                    .single()

                if (!profile?.onboarding_completed) {
                    // Track onboarding start
                    trackEvent(events.USER_ONBOARDING_STARTED, {
                        user_id: session.user.id,
                        is_new_user: session.user.created_at === session.user.updated_at,
                        ...getUtmParams() // Add UTM parameters to tracking event
                    })
                    setIsLoading(false)
                    navigate('/onboarding')
                } else {
                    setIsLoading(false)
                    navigate('/dashboard')
                }
            }
        })

        checkAuthStatus()

        return () => {
            authListener.subscription.unsubscribe()
        }
    }, [navigate, location])

    if (isLoading) {
        return <div>Loading...</div>
    }

    return <div>Authentication failed. Please try again.</div>
}